import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropdownProps, OptionPosition, SelectPickerValue } from './interfaces';
import styles from './index.module.scss';
import Typography from '@primitives/Typography';
import { SelectPickerOptionProps } from '@components/SelectPicker/types';
import ArrowDownIcon from '@icons/ArrowDownIcon';
import ArrowUpIcon from '@icons/ArrowUpIcon';
import { useKeyListener, useOutsideClick } from '@hooks';
import cx from 'classnames';
import SelectPicker from './SelectPicker';
import { ESCAPE_KEY } from '@navi/web-ui/lib/constants/Components';
import { ArrowDownSolidIcon, ArrowUpSolidIcon } from '@navi/web-ui/lib/icons';
import EllipsisTooltipPlaceholder from './EllipsisTooltipPlaceholder';
import CancelCircleFilled from '@cp/src/assets/icons/CancelCircledFilled';
import Button from '@navi/web-ui/lib/primitives/Button/Button';

const SelectedValueWithSource = (props: {
  selectedDropdownValue: SelectPickerOptionProps[];
  placeholder: string;
  customOptionTemplate?: (option: string) => JSX.Element;
}) => {
  const { selectedDropdownValue, placeholder } = props;
  const { label, source } =
    (selectedDropdownValue?.[0] as unknown as { label: string; source: string }) || {};

  if (!label) {
    return <>{placeholder}</>;
  }
  return (
    <Typography variant="p3">
      {label}{' '}
      {source ? (
        <Typography variant="p3" as="span" className={styles.greyText}>
          ({source})
        </Typography>
      ) : null}
    </Typography>
  );
};

const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  selectedValue,
  onSelectionChange,
  placeholder = 'Select option',
  containerClasses,
  error,
  disabled,
  multiSelect,
  defaultSelected = null,
  arrowIconColor = styles.naviIconDefaultColor,
  showSourceInSelectedValue,
  arrowIconWidth = 16,
  showSourceAtBottom = false,
  selectedLabels = '',
  innerContainerStyle = '',
  optionPosition = OptionPosition.bottom,
  customOptionTemplate,
  placeholderColor = '',
  showSolidArrow = false,
  showEllipsisTooltip = false,
  pickerContainerClasses,
  showTextCursorOnDisabled = false,
  tabIndexEnabled = false,
  selectPickerItemClass = '',
  titleClass = '',
  showCancelOption = false,
  showTitleInSelectedValue = false,
  onRemoveFilters,
  ...restProps
}) => {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<
    Map<SelectPickerValue, SelectPickerOptionProps>
  >(defaultSelected ? new Map().set(defaultSelected.value, defaultSelected) : new Map([]));
  const filterRef = useRef(null);

  useEffect(() => {
    selectedDropdownValue.clear();
    setSelectedDropdownValue(new Map([]));
    if (!selectedValue) {
      return;
    }
    if (Array.isArray(selectedValue)) {
      selectedValue.forEach(value => {
        const selectedOption = options.find(opt => opt?.value === value);
        if (selectedOption) {
          addSelectedValue(value, selectedOption);
        }
      });
    } else {
      const selectedOption = options.find(opt => opt.value === selectedValue);
      if (selectedOption) {
        addSelectedValue(selectedValue, selectedOption);
      }
    }
  }, [selectedValue]);

  const addSelectedValue = useCallback(
    (value: SelectPickerValue, option: SelectPickerOptionProps) => {
      setSelectedDropdownValue(new Map(selectedDropdownValue.set(value, option)));
    },
    []
  );

  const togglePicker = () => {
    if (disabled) {
      return;
    }
    setShowPicker(!showPicker);
  };

  const handleSingleSelect = useCallback(
    (value: SelectPickerValue, option: SelectPickerOptionProps) => {
      selectedDropdownValue.clear();
      setSelectedDropdownValue(new Map([[value, option]]));
      onSelectionChange(option);
      setShowPicker(false);
    },
    []
  );

  const handleMultiSelect = useCallback(
    (value: SelectPickerValue, option: SelectPickerOptionProps) => {
      // check map => if value already present then delete else add
      if (selectedDropdownValue.has(value)) {
        selectedDropdownValue.delete(value);
        setSelectedDropdownValue(new Map(selectedDropdownValue));
      } else {
        addSelectedValue(value, option);
      }
      const selectedKeys = Array.from(selectedDropdownValue.keys());
      const sortedKeys = selectedKeys.sort();
      onSelectionChange(sortedKeys);

      // create array of map value
      // const newSelectedList: SelectPickerOptionProps[] = Array.from(selectedDropdownValue.values());
      // onSelectionChange(newSelectedList);
    },
    []
  );

  const handleSelectionChange = useCallback((option: SelectPickerOptionProps) => {
    const { value, isDisabled } = option;

    // nothing if disabled
    if (isDisabled) {
      return;
    }

    if (multiSelect) {
      handleMultiSelect(value, option);
      return;
    }
    handleSingleSelect(value, option);
  }, []);

  useOutsideClick(filterRef, () => setShowPicker(false));

  useKeyListener(ESCAPE_KEY, () => setShowPicker(false), showPicker);

  const ArrowIcon = showPicker ? ArrowUpIcon : ArrowDownIcon;
  const SolidArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;

  const getSelectedValuesString = useMemo(() => {
    return Array.from(selectedDropdownValue.values())
      .map(opt => opt.label)
      .join(', ');
  }, [selectedDropdownValue]);

  const selectedValuesArray = useMemo(() => {
    return Array.from(selectedDropdownValue.values());
  }, [selectedDropdownValue]);

  useEffect(() => {
    if (defaultSelected && multiSelect) {
      setSelectedDropdownValue(defaultSelected);
      return;
    }
    if (defaultSelected) {
      setSelectedDropdownValue(new Map().set(defaultSelected.value, defaultSelected));
      return;
    }
    // if (!multiSelect) {
    //   setSelectedDropdownValue(new Map([]));
    // }
  }, [options]);

  const eclipseString = (str: string, length: number) => {
    if (str.length > length) {
      return str.substring(0, length) + '...';
    } else {
      return str;
    }
  };

  const removeFilters = (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedDropdownValue(new Map([]));
  };

  const isShowCancelOption = selectedDropdownValue.size > 0 && showCancelOption;

  return (
    <div
      tabIndex={tabIndexEnabled ? 0 : -1}
      className={cx(
        styles['dropdown-container'],
        { [styles['dropdown-container--disabled']]: disabled },
        containerClasses
      )}
      ref={filterRef}
    >
      <div
        className={cx(styles['dropdown'], innerContainerStyle, {
          [styles['dropdown--active']]: showPicker,
          [styles['dropdown--error']]: error,
          [showTextCursorOnDisabled
            ? styles['dropdown--disabled-cursor-allowed']
            : styles['dropdown--disabled']]: disabled
        })}
        onClick={togglePicker}
      >
        <Typography variant="p3" className={styles['dropdown--title']}>
          {showEllipsisTooltip && getSelectedValuesString.length > 0 ? (
            <EllipsisTooltipPlaceholder
              text={getSelectedValuesString || placeholder}
              disabled={disabled}
            />
          ) : !multiSelect && showSourceInSelectedValue ? (
            <SelectedValueWithSource
              selectedDropdownValue={selectedValuesArray}
              placeholder={placeholder}
            />
          ) : placeholderColor && getSelectedValuesString.length === 0 ? (
            <Typography variant="p3" color={placeholderColor} className={titleClass}>
              {getSelectedValuesString || placeholder}
            </Typography>
          ) : (
            <Typography
              variant="p3"
              color={disabled ? 'var(--navi-bordered-input-hint-text-wrapper-text)' : ''}
              className={titleClass}
            >
              {showTitleInSelectedValue && getSelectedValuesString && (
                <Typography variant="p3" className={titleClass}>
                  {placeholder}:
                </Typography>
              )}
              {getSelectedValuesString || placeholder}{' '}
              <span className={styles.selectedPhoneSource}>
                {`${selectedLabels && '(' + eclipseString(selectedLabels, 16) + ')'}`}
              </span>{' '}
            </Typography>
          )}
        </Typography>
        {isShowCancelOption && (
          <Button variant="text" onClick={onRemoveFilters || removeFilters}>
            <CancelCircleFilled width={16} fillColor="var(--grayscale-3)" />
          </Button>
        )}
        <div>
          {showSolidArrow ? (
            <SolidArrowIcon
              width={arrowIconWidth}
              color={arrowIconColor}
              className={styles['dropdown--arrowIcon']}
            />
          ) : (
            <ArrowIcon
              width={arrowIconWidth}
              color={arrowIconColor}
              className={styles['dropdown--arrowIcon']}
            />
          )}
        </div>
      </div>
      {error && typeof error === 'string' && (
        <Typography variant="p5" className={styles.errorMsg}>
          {error}
        </Typography>
      )}
      {showPicker && (
        <SelectPicker
          options={options}
          selectedValue={selectedDropdownValue}
          onSelectionChange={handleSelectionChange}
          wrapperClasses={cx(styles['picker'], pickerContainerClasses)}
          multiSelect={multiSelect}
          showSourceAtBottom={showSourceAtBottom}
          optionPosition={optionPosition}
          customOptionTemplate={customOptionTemplate}
          selectPickerItemClass={selectPickerItemClass}
          {...restProps}
        />
      )}
    </div>
  );
};

export default Dropdown;
